import {
  appendCheckpoint,
  finishTracking,
  startTracking,
  type TrackingKey,
} from '@heidi/runtime/performance';

/** Time-to-first-token checkpoint emitted by every template stream run. */
export const FIRST_TOKEN_CHECKPOINT = 'first_token';

/**
 * Wraps a template stream so a single instanced perf run is reported for it.
 *
 * Starts an instanced tracking when the stream begins, exposes a `markFirstToken`
 * callback to checkpoint time-to-first-token, and finishes the run on both the
 * success and error paths before rethrowing. Instanced so the structure and
 * example streams (which run concurrently during initial generation, and repeat
 * across chat edits) never clobber each other.
 */
export const trackStreamRun = async <T>(
  key: TrackingKey,
  metadata: Record<string, unknown> | undefined,
  run: (markFirstToken: () => void) => Promise<T>
): Promise<T> => {
  const handle = startTracking({ key, instanced: true, metadata });
  const markFirstToken = () =>
    appendCheckpoint({ handle, checkpointName: FIRST_TOKEN_CHECKPOINT });

  try {
    const result = await run(markFirstToken);
    finishTracking({ handle, metadata: { result: 'success' } });
    return result;
  } catch (error) {
    finishTracking({ handle, metadata: { result: 'error' } });
    throw error;
  }
};
